import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { MaskedLines, Fade, Overline } from "@/components/Reveal";
import { POLITICAL_AREAS, IMAGES } from "@/data/content";

const PoliticalConsulting = () => (
  <>
    <SEO
      title="Political Consulting & Campaign Communications | Mosaicon Strategies — Toronto & Ontario"
      description="Political consulting and campaign communications for candidates and political organizations at the municipal, provincial and federal levels in Canada — strategy, messaging, media, community outreach and campaign execution."
    />

    {/* Dark teal hero — serious, non-partisan */}
    <section className="bg-teal text-cream relative overflow-hidden" data-testid="political-hero">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 md:pt-28 md:pb-32 relative">
        <Fade y={12}>
          <p className="text-xs uppercase tracking-[0.3em] font-medium text-gold" data-testid="political-overline">
            Political Consulting
          </p>
        </Fade>
        <h1 className="mt-6 font-serif text-4xl md:text-6xl tracking-tighter leading-[1.08] max-w-4xl" data-testid="political-heading">
          <MaskedLines delay={0.15} lines={["Political strategy.", "Communications.", "Campaign execution."]} />
        </h1>
        <Fade delay={0.35} className="mt-10 max-w-3xl space-y-5 text-base md:text-lg leading-relaxed text-cream/80" data-testid="political-intro">
          <p>
            Successful political campaigns require more than a candidate, a message and an election date. They
            require strategy, organization, discipline, relationships and a detailed understanding of the
            communities a candidate hopes to represent.
          </p>
          <p>
            Mosaicon Strategies provides political consulting and campaign communications services for candidates
            and political organizations at the municipal, provincial and federal levels in Canada.
          </p>
          <p>
            Our approach brings together campaign strategy, communications, media relations, stakeholder
            engagement, community outreach and campaign execution.
          </p>
        </Fade>
      </div>
    </section>

    {/* Areas */}
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32" data-testid="political-areas">
      <Overline>Areas of Counsel</Overline>
      <div className="mt-12 grid md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
        {POLITICAL_AREAS.map((area, i) => (
          <Fade key={area.title} delay={(i % 2) * 0.08} y={20}>
            <div className="h-full bg-white p-8 md:p-12" data-testid={`political-area-${i + 1}`}>
              <p className="font-serif text-sm text-burgundy/60">{String(i + 1).padStart(2, "0")}</p>
              <h2 className="mt-4 font-serif text-2xl md:text-3xl tracking-tight text-ink">{area.title}</h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-ink/65">{area.copy}</p>
            </div>
          </Fade>
        ))}
      </div>
    </section>

    {/* Image strip */}
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 md:pb-32">
      <Fade>
        <div className="overflow-hidden">
          <img
            src={IMAGES.podium}
            alt="A speaker addressing an audience at a public forum"
            className="w-full aspect-[21/9] object-cover"
            loading="lazy"
            data-testid="political-image"
          />
        </div>
        <p className="mt-4 text-xs uppercase tracking-[0.2em] text-ink/40">
          Civic engagement, community dialogue and public life in the GTA
        </p>
      </Fade>
    </section>

    {/* Compliance statement */}
    <section className="bg-sand/60 border-y border-ink/10" data-testid="political-compliance">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 md:py-20 text-center">
        <Fade>
          <p className="font-serif text-xl md:text-2xl leading-relaxed text-ink tracking-tight">
            Political consulting engagements are undertaken selectively and subject to availability, conflict
            review and applicable election laws and regulations.
          </p>
        </Fade>
      </div>
    </section>

    {/* CTA */}
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-28 text-center" data-testid="political-cta">
      <Fade>
        <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-ink">
          Preparing for a campaign begins long before election day.
        </h2>
        <Link
          to="/contact"
          data-testid="political-contact-btn"
          className="group mt-10 inline-flex items-center gap-3 bg-burgundy text-cream text-xs uppercase tracking-[0.22em] font-bold px-10 py-5 transition-colors duration-300 hover:bg-ink"
        >
          Request a Confidential Consultation
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Fade>
    </section>
  </>
);

export default PoliticalConsulting;
